import _isEmpty from 'lodash/isEmpty';

import { RB_DATA, RB_DATA_FIELD, RB_DEFAULT_KEY } from '../info';

import { ResumeBuilderSchema } from './schema';

export const getFilledAndUnFilledSteps = (currentRBState = {}) => {
  const allSteps = [
    ...Object.values(currentRBState).slice(3, 5),
    ...Object.values(currentRBState).slice(6, 10),
  ].map(({ index, navLabel, data }) => ({ index, navLabel, data }));

  const otherStepIndex = allSteps.findIndex((step) => step.index === 8);

  // First Delete Other Step
  if (otherStepIndex !== -1) {
    allSteps.splice(otherStepIndex, 1);
  }

  let unfilledSteps = allSteps.filter((step) => _isEmpty(step.data));

  let filledSteps = allSteps.filter((step) => !_isEmpty(step.data));

  const certData = currentRBState[8].data[RB_DATA.EXTRA_CERTIFICATIONS] || {};

  const languageData = currentRBState[8].data[RB_DATA.EXTRA_LANGUAGES] || {};

  if (_isEmpty(certData)) {
    unfilledSteps.push({
      index: 8,
      subIndex: 1,
      navLabel: 'Certifications',
    });
  } else {
    filledSteps.push({
      index: 8,
      subIndex: 1,
      navLabel: 'Certifications',
    });
  }

  if (_isEmpty(languageData)) {
    unfilledSteps.push({
      index: 8,
      subIndex: 2,
      navLabel: 'Languages',
    });
  } else {
    filledSteps.push({
      index: 8,
      subIndex: 2,
      navLabel: 'Languages',
    });
  }

  const customSections = Object.values(currentRBState[8]?.data?.customSections || {}).map(
    (customSection, idx) => ({
      index: 8,
      subIndex: 3 + idx,
      navLabel: customSection.sectionName,
      value: customSection.details,
      imgName: 'CustomSection',
    })
  );

  filledSteps.push(...customSections.filter((section) => section.value));

  unfilledSteps.push(...customSections.filter((section) => !section.value));

  return { filledSteps, unfilledSteps };
};

export const getRequiredFields = (currentRBState, theme) => {
  let requiredFields = {};
  const contactDetails = currentRBState[3].data[RB_DATA.HEADER]?.[RB_DEFAULT_KEY.HEADER] || {};
  const firstName = contactDetails[RB_DATA_FIELD.HEADER.FIRST_NAME] || '';
  const lastName = contactDetails[RB_DATA_FIELD.HEADER.LAST_NAME] || '';
  const email = contactDetails[RB_DATA_FIELD.HEADER.EMAIL] || '';
  if (firstName && lastName && email) {
    requiredFields = Object.assign(requiredFields, {
      firstName,
      lastName,
      email,
      resumeTemplate: theme || localStorage.getItem('rb-color-theme') || 'ColorTheme1',
    });
  }
  return requiredFields;
};

export const getRBFlow = ({
  currentStepIndex = 0,
  currentSubStepIndex = 0,
  currentRBState = ResumeBuilderSchema,
  user = { isLoggedIn: false },
}) => {
  // Next Redux state for resume builder flow
  let nextStepIndex = currentStepIndex;
  let nextSubStepIndex = currentSubStepIndex;
  let nextBtnLabel = 'Next';
  let errorFields = {};

  // Conditional Navigation Flow Variables
  const isUserSignedIn = !!(user && user.isLoggedIn);

  const extraStepSubSteps = currentRBState[8].subSteps || {};

  const extraStepSubStepsLength = Object.keys(extraStepSubSteps).length;

  const otherStepData =
    currentRBState[8]?.data?.[RB_DATA.EXTRA]?.[RB_DEFAULT_KEY.EXTRA_DEFAULT] || {};

  const certificationsSelected = otherStepData[RB_DATA_FIELD.EXTRA.INCLUDE_CERTIFICATIONS];

  const languagesSelected = otherStepData[RB_DATA_FIELD.EXTRA.INCLUDE_LANGUAGES];

  const customSections = currentRBState[8]?.data?.[RB_DATA.EXTRA_CUSTOM] || {};

  const oneOrMoreEmployementHistory = !!Object.values(
    currentRBState[4].data?.[RB_DATA.WORK_HISTORY] || {}
  ).length;

  const educationalDetails = Object.values(currentRBState[6].data?.[RB_DATA.EDUCATION] || {});

  const workHistoryErrorFields = currentRBState[4]?.errorFields || {};

  const oneOrMoreEducationHistory = !!educationalDetails?.length;

  const hasSkills = !_isEmpty(currentRBState[7].data?.[RB_DATA.SKILLS] || {});

  const hasSummary = !_isEmpty(currentRBState[9].data?.[RB_DATA.SUMMARY]);

  const degreeNotHighSchool = educationalDetails.some((eduDetail) => {
    const degreeValue = eduDetail[RB_DATA_FIELD.EDUCATION.DEGREE] || '';
    return degreeValue && degreeValue !== 'None' && degreeValue !== 'High School';
  });

  const getNextBtnLabel = (label = '') => `Next${label ? `: ${label}` : ''}`;

  const onNext = () => {
    switch (currentStepIndex) {
      case 0:
        switch (currentSubStepIndex) {
          case 0:
            nextStepIndex += 2;
            nextSubStepIndex = 0;
            nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 2]?.btnLabel);
            break;
          default:
            nextSubStepIndex = 1;
            break;
        }
        break;
      case 3:
        if (oneOrMoreEmployementHistory) {
          nextSubStepIndex = 3;
        } else {
          nextSubStepIndex = 0;
        }
        nextStepIndex += 1;
        if (isUserSignedIn && oneOrMoreEmployementHistory) {
          nextBtnLabel = 'Next: Education';
        }
        break;
      case 4:
        switch (currentSubStepIndex) {
          case 3:
            if (isUserSignedIn) {
              nextStepIndex += 2;
              if (oneOrMoreEducationHistory) {
                nextSubStepIndex = 3;
                nextBtnLabel = 'Next: Skills';
              } else {
                nextSubStepIndex = 0;
              }
            } else {
              nextStepIndex += 1;
              nextSubStepIndex = 0;
            }
            break;
          case 2:
            nextSubStepIndex += 1;
            nextBtnLabel = 'Next: Education';
            break;
          default:
            nextSubStepIndex += 1;
        }
        break;
      case 5:
        if (isUserSignedIn) {
          if (oneOrMoreEducationHistory) {
            nextSubStepIndex = 3;
          } else {
            nextSubStepIndex = 0;
          }
          nextStepIndex += 1;
        }
        break;
      case 6:
        switch (currentSubStepIndex) {
          case 1:
            if (degreeNotHighSchool) {
              nextSubStepIndex += 1;
            } else {
              if (hasSkills) {
                nextSubStepIndex = 1;
              } else {
                nextSubStepIndex = 0;
              }
              nextBtnLabel = 'Next: Other';
              nextStepIndex += 1;
            }
            break;
          case 3:
            if (hasSkills) {
              nextSubStepIndex = 1;
              nextBtnLabel = 'Next: Other';
            } else {
              nextSubStepIndex = 0;
            }
            nextStepIndex += 1;
            break;
          case 2:
            nextSubStepIndex += 1;
            nextBtnLabel = 'Next: Skills';
            break;
          default:
            nextSubStepIndex += 1;
        }
        break;
      case 7:
        switch (currentSubStepIndex) {
          case 0:
            nextSubStepIndex += 1;
            nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 1]?.btnLabel);
            break;
          default:
            nextSubStepIndex = 0;
            nextStepIndex += 1;
        }
        break;
      case 8:
        switch (currentSubStepIndex) {
          case 0:
            if (certificationsSelected) {
              nextSubStepIndex += 1;
            } else if (languagesSelected) {
              nextSubStepIndex += 2;
              nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 1]?.btnLabel);
            } else if (extraStepSubStepsLength > 3) {
              nextSubStepIndex = 3;
            } else {
              if (hasSummary) {
                nextSubStepIndex = 1;
                nextBtnLabel = 'Next: Preview';
              } else {
                nextSubStepIndex = 0;
              }
              nextStepIndex += 1;
            }

            const customSectionsErrors = Object.values(customSections).reduce((acc, item) => {
              if (item.sectionName.trim() !== '') return acc;

              return [
                ...acc,
                {
                  sectionId: item.sectionId,
                  error: 'Please enter a section name to proceed.',
                },
              ];
            }, []);

            if (extraStepSubStepsLength > 2 && customSectionsErrors.length) {
              errorFields['customSection'] = customSectionsErrors;
              nextSubStepIndex = 0;
            } else {
              errorFields['customSection'] = '';
            }
            break;
          case 1:
            if (languagesSelected) {
              nextSubStepIndex += 1;
              nextBtnLabel = getNextBtnLabel(
                extraStepSubStepsLength > 3 ? extraStepSubSteps[3]?.title : 'Summary'
              );
            } else if (extraStepSubStepsLength > 3) {
              nextSubStepIndex = 3;
              nextBtnLabel = getNextBtnLabel(
                extraStepSubSteps[nextSubStepIndex + 1]?.title || 'Summary'
              );
            } else {
              if (hasSummary) {
                nextSubStepIndex = 1;
                nextBtnLabel = 'Next: Preview';
              } else {
                nextSubStepIndex = 0;
              }
              nextStepIndex += 1;
            }
            break;
          case 2:
            if (extraStepSubStepsLength > 3) {
              nextSubStepIndex = 3;
              nextBtnLabel = getNextBtnLabel(
                extraStepSubSteps[nextSubStepIndex + 1]?.title || 'Summary'
              );
            } else if (hasSummary) {
              nextSubStepIndex = 1;
              nextBtnLabel = 'Next: Preview';
              nextStepIndex += 1;
            } else {
              nextSubStepIndex = 0;
              nextStepIndex += 1;
            }
            break;
          default:
            if (currentSubStepIndex >= 3 && currentSubStepIndex < extraStepSubStepsLength - 1) {
              nextSubStepIndex += 1;
              nextBtnLabel = getNextBtnLabel(
                extraStepSubSteps[nextSubStepIndex + 1]?.title || 'Summary'
              );
            } else if (hasSummary) {
              nextSubStepIndex = 1;
              nextBtnLabel = 'Next: Preview';
              nextStepIndex += 1;
            } else {
              nextSubStepIndex = 0;
              nextStepIndex += 1;
            }
        }
        break;
      case 9:
        switch (currentSubStepIndex) {
          case 0:
            nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 1]?.btnLabel);
            nextSubStepIndex += 1;
            break;
          default:
            nextStepIndex += 1;
            nextSubStepIndex = 0;
        }
        break;
      default:
        nextStepIndex += 1;
        nextSubStepIndex = 0;
        nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 1]?.btnLabel);
    }

    // Will return next step index and next substep index
    return {
      nextStepIndex,
      nextSubStepIndex,
      nextBtnLabel,
      errorFields,
    };
  };

  const onPrev = () => {
    switch (currentStepIndex) {
      case 2:
        nextStepIndex -= 2;
        nextSubStepIndex = 1;
        nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 2]?.btnLabel);
        break;
      case 4:
        switch (currentSubStepIndex) {
          case 1:
            if (oneOrMoreEmployementHistory && _isEmpty(workHistoryErrorFields)) {
              nextSubStepIndex = 3;
            } else {
              nextSubStepIndex = 0;
            }
            break;
          case 0:
          case 3:
            nextStepIndex -= 1;
            nextSubStepIndex = 0;
            break;
          default:
            nextSubStepIndex -= 1;
            nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 1]?.btnLabel);
        }
        break;
      case 5:
        if (oneOrMoreEmployementHistory) {
          nextSubStepIndex = 3;
        } else {
          nextSubStepIndex = 0;
        }
        nextStepIndex -= 1;
        break;
      case 6:
        switch (currentSubStepIndex) {
          case 1:
            nextSubStepIndex -= 1;
            break;
          case 2:
            if (oneOrMoreEducationHistory) {
              nextSubStepIndex += 3;
            } else {
              nextSubStepIndex -= 1;
            }
          default:
            if (oneOrMoreEmployementHistory) {
              nextSubStepIndex = 3;
            } else {
              nextSubStepIndex = 0;
            }
            nextStepIndex -= 2;
            break;
        }
        break;
      case 7:
        switch (currentSubStepIndex) {
          case 0:
          case 1:
            if (oneOrMoreEducationHistory) {
              nextSubStepIndex = 3;
            } else {
              nextSubStepIndex = 1;
            }
            nextStepIndex -= 1;
            break;
        }
        break;
      case 8:
        switch (currentSubStepIndex) {
          case 0:
            nextSubStepIndex = 1;
            nextStepIndex -= 1;
            break;
          case 1:
            nextSubStepIndex -= 1;
            break;
          case 2:
            if (certificationsSelected) {
              nextSubStepIndex -= 1;
            } else {
              nextSubStepIndex -= 2;
            }
            break;
          default:
            if (currentSubStepIndex > 3) {
              nextSubStepIndex -= 1;
            } else if (languagesSelected) {
              nextSubStepIndex = 2;
            } else if (certificationsSelected) {
              nextSubStepIndex = 1;
            } else {
              nextSubStepIndex = 0;
            }
        }
        break;
      case 9:
        if (extraStepSubStepsLength > 3) {
          nextSubStepIndex = extraStepSubStepsLength - 1;
        } else if (languagesSelected) {
          nextSubStepIndex = 2;
        } else if (certificationsSelected) {
          nextSubStepIndex = 1;
        } else {
          nextSubStepIndex = 0;
        }
        nextStepIndex -= 1;
        break;
      default:
        nextStepIndex -= 1;
        nextSubStepIndex = 0;
        nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 1]?.btnLabel);
    }

    // Will return next step index and next substep index
    return {
      nextStepIndex,
      nextSubStepIndex,
      nextBtnLabel,
    };
  };

  const onTargetStep = (targetStepIndex, targetSubStepIndex = 0) => {
    let nextStepIndex = targetStepIndex;
    let nextSubStepIndex = targetSubStepIndex;
    let nextBtnLabel = 'Next';
    let nextToNextStep = currentRBState[nextStepIndex + 1];

    switch (targetStepIndex) {
      case 3:
        nextBtnLabel = 'Next: Work History';
        break;
      case 4:
        if (!isUserSignedIn) {
          nextBtnLabel = 'Next';
        } else if (oneOrMoreEmployementHistory) {
          nextToNextStep = currentRBState[nextStepIndex + 2];
          nextBtnLabel = `Next${
            nextStepIndex >= 0 && !!nextToNextStep ? `: ${nextToNextStep.btnLabel}` : ''
          }`;
        }
        nextSubStepIndex = 3;
        break;
      case 5:
        if (isUserSignedIn) {
          nextStepIndex += 1;
        }
        break;
      case 6:
        if (oneOrMoreEducationHistory) {
          nextSubStepIndex = 3;
          nextBtnLabel = `Next${
            nextStepIndex >= 0 && !!nextToNextStep ? `: ${nextToNextStep.btnLabel}` : ''
          }`;
        }
        break;
      case 7:
      case 9:
        nextSubStepIndex = 1;
        nextBtnLabel = `Next${
          nextStepIndex >= 0 && !!nextToNextStep ? `: ${nextToNextStep.btnLabel}` : ''
        }`;
        break;
      case 8:
        switch (targetSubStepIndex) {
          case 2:
            nextBtnLabel = `Next${
              nextStepIndex >= 0 && !!nextToNextStep ? `: ${nextToNextStep.btnLabel}` : ''
            }`;
            break;
        }
    }

    return {
      nextStepIndex,
      nextSubStepIndex,
      nextBtnLabel,
    };
  };

  const navigateStepByStepUrl = (stepUrl) => {
    let stepIndex = 0;
    let subStepIndex = 0;
    const steps = Object.values(currentRBState);
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const subSteps = Object.values(step.subSteps);
      if (subSteps.some((subStep) => subStep.url === stepUrl)) {
        subStepIndex = subSteps.findIndex((subStep) => subStep.url === stepUrl);
        stepIndex = step.index;
        break;
      }
    }
    return onTargetStep(stepIndex, subStepIndex);
  };

  return {
    onNext,
    onPrev,
    onTargetStep,
    navigateStepByStepUrl,
  };
};
